import React, { useEffect, useState, Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
type TaxItem = {
  num: string;
  anchor: string;
  name: string;
  definition: string;
  feelsLike: string;
  whereShowsUp: string;
  whatItCauses: string;
  commonMisread: string;
  validate: string[];
};
export function WhatsWrong() {
  const [expandedTax, setExpandedTax] = useState<number | null>(null);
  const location = useLocation();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const taxes: TaxItem[] = [{
    num: '1',
    anchor: 'dependence',
    name: 'Dependence Tax',
    definition: 'Too much still depends on the founder or a few key people.',
    feelsLike: 'If the right person is out, progress slows or stops.',
    whereShowsUp: 'Qualification, approvals, handoffs, exception handling, delivery judgment, and expansion decisions that still depend on a few people carrying the logic.',
    whatItCauses: 'Slower movement, inconsistent decisions, stalled handoffs, founder pull, and revenue loss or missed upside whenever the right person is not in the loop.',
    commonMisread: 'It looks like a people problem. It is often trapped logic that never made it into shared rules, workflows, or decision structure.',
    validate: ['Where important decisions still rely on memory, interpretation, or one person carrying too much context.']
  }, {
    num: '2',
    anchor: 'opportunity',
    name: 'Opportunity Tax',
    definition: 'Leadership has no room for the future because today keeps eating all the oxygen.',
    feelsLike: 'Strategic work keeps getting pushed because the business cannot run without constant intervention.',
    whereShowsUp: 'Anywhere leadership keeps getting pulled back into conversion issues, customer problems, reporting questions, delivery friction, or escalations instead of redesigning what is next.',
    whatItCauses: 'Strategic delay, reactive leadership, constant context switching, and missed revenue because the business is too busy catching what should not have broken.',
    commonMisread: 'It looks like a planning problem. It is often a load-path problem.',
    validate: ['What keeps routing important work back into the urgent pile.']
  }, {
    num: '3',
    anchor: 'leakage',
    name: 'Leakage Tax',
    definition: 'Revenue and margin leak through exceptions, rework, unclear scope, and work that should have resolved earlier.',
    feelsLike: 'Revenue is coming in, but profitability does not follow the way it should.',
    whereShowsUp: 'Qualification, promises, scoping, handoffs, delivery, retention, and anywhere unmanaged variance keeps entering the system.',
    whatItCauses: 'Bad-fit sales, rework, margin erosion, cleanup, downstream churn, and missed expansion revenue because the business is too busy catching what should not have broken.',
    commonMisread: 'It looks like pricing. Sometimes it is. Often it is unmanaged variance and weak journey logic.',
    validate: ['Where exceptions enter, how they become normal, and where profit is quietly being lost.']
  }, {
    num: '4',
    anchor: 'visibility',
    name: 'Visibility Tax',
    definition: 'The numbers create debate instead of decisions.',
    feelsLike: 'Leaders cannot tell what is true fast enough to move with confidence.',
    whereShowsUp: 'Lead flow, conversion, delivery quality, churn patterns, expansion opportunities, and performance reporting that should guide action but does not.',
    whatItCauses: 'Slow decisions, missed signals, weak learning, debate instead of action, and too much leadership energy spent validating reality before moving to capture revenue.',
    commonMisread: 'It looks like a dashboard problem. It is often a definitions problem.',
    validate: ['Which metrics are not trusted, where definitions drift, and what has to be reconciled manually before decisions can happen.']
  }, {
    num: '5',
    anchor: 'capability',
    name: 'Capability Tax',
    definition: 'Managers cannot carry the load cleanly, so complexity routes upward.',
    feelsLike: 'Leaders spend all day translating, resolving, and stepping back into work that should not need them.',
    whereShowsUp: 'Cross-functional coordination, handoffs, exception handling, customer movement, and day-to-day decisions that managers cannot carry cleanly without escalation.',
    whatItCauses: 'Stalled work, slow execution, too much translation, leadership becoming the routing layer, and a business that cannot scale revenue cleanly through the middle.',
    commonMisread: 'It looks like weak management. It is often unclear ownership and weak decision structure under real complexity.',
    validate: ['Where accountability breaks down under load and where managers do not have the structure needed to carry the weight.']
  }, {
    num: '6',
    anchor: 'technology',
    name: 'Technology Tax',
    definition: 'The stack exists, but it does not create real leverage.',
    feelsLike: 'You keep buying software and still end up doing the work the hard way.',
    whereShowsUp: 'Routing, reporting, workflow execution, automation, AI use cases, and system adoption where the tools exist but the underlying logic is still too unclear.',
    whatItCauses: 'Tool sprawl, brittle automation, workarounds, wasted software spend, and AI that produces output without revenue leverage or upside.',
    commonMisread: 'It looks like the wrong tools. Often the bigger issue is unclear rules, vague ownership, and workflows that were never redesigned.',
    validate: ['Whether the business logic is clear enough for automation or AI to help at all.']
  }];
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taxParam = params.get('tax')?.toLowerCase() ?? '';
    const hash = (location.hash || '').replace('#', '').toLowerCase();
    const target = taxParam || hash;
    if (!target) return;
    const idx = taxes.findIndex(t => t.anchor === target);
    if (idx === -1) return;
    setExpandedTax(idx);
    setTimeout(() => {
      const el = document.getElementById(target);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }, [location.search, location.hash]);
  return <main id="top" className="w-full" data-id="element-623" data-mp="2vvbday0bujia">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-624" data-mp="2gzlt95epcgg7">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-625" data-mp="2lxnbxp87udr5">
          <BlueprintGrid data-id="element-626" data-mp="2lzzy6el0baa0" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-627" data-mp="285yx3e6l1b6h">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-628" data-mp="2cz1wi2y1jeul">
            What&apos;s Wrong
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-629" data-mp="236w08h0rab9l">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-630" data-mp="1p9r0eyrmy8ms">
              If too much revenue is getting stuck, lost, or missed across the
              journey,
            </span>{' '}
            <span className="text-navy" data-id="element-631" data-mp="24pxfkkkw4u0c">
              the underlying failure usually shows up in a few recognizable
              patterns.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-632" data-mp="1zsdy7bswc146">
            <p data-id="element-633" data-mp="1qq4ryy4nutwl">
              You feel it when leads stall, handoffs break, delivery inherits
              shaky promises, expansion gets missed, and growth starts exposing
              old ways of working that no longer fit how customers actually
              move.
            </p>
            <p className="font-medium text-navy" data-id="element-634" data-mp="1c18j4yus5cux">
              The visible symptom is rarely the whole story.
            </p>
            <p data-id="element-635" data-mp="170ud1mcqoczj">This page is here to help you see the patterns underneath it.</p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6" data-id="element-636" data-mp="131ljahhp8f3z">
            <div className="flex flex-col" data-id="element-637" data-mp="3epqddyv39ye1">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-638" data-mp="3iohf6jai247h">
                <span data-id="element-639" data-mp="3slcbcvjavsil">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-640" data-mp="2v0g0esmq9rjm" />
              </Link>
              <p className="text-sm text-text-muted mt-2" data-id="element-641" data-mp="2zyrbf6ore83f">
                45 minutes. No pitch. Just clarity.
              </p>
            </div>

            <div className="flex flex-col" data-id="element-642" data-mp="34x2khf78cekl">
              <a href="#six-taxes" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-643" data-mp="39okipvhvc11v">
                <span data-id="element-644" data-mp="2h6yp1v473l4o">Jump to the Six Structural Taxes</span>
                <ChevronDown className="w-4 h-4" data-id="element-645" data-mp="2xa9ngukmt5gl" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Where revenue usually gets stuck, lost, or missed */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-646" data-mp="377nwwa850tze">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-647" data-mp="3c7a6m9orbs3g">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-648" data-mp="2jjed1duhigaw">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-649" data-mp="2ohzfljx7lc2i">
              Where revenue usually gets stuck, lost, or missed
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-650" data-mp="1vtk1czlwixs8">
            <p className="text-text" data-id="element-651" data-mp="1vqxuz3aqyobl">
              Revenue usually breaks down where the journey no longer matches
              reality.
            </p>
            <p className="text-text" data-id="element-652" data-mp="hvz80yhjyt60">That often looks like:</p>

            <ul className="space-y-3 pl-1" data-id="element-653" data-mp="dvf9prl8tjia">
              {['the wrong customers get sold', 'leads or customers stall in the middle', 'handoffs break between teams, stages, or systems', 'what gets promised drifts from what delivery can actually support', 'pricing or packaging no longer fits the customer or the work', 'churn starts earlier than leadership thinks', 'expansion opportunities go unseen', 'leaders cannot get clean enough signal to see what is actually happening'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-654" data-mp="3hdfaomsdsome" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-655" data-mp="3cdkjsj8lvz1c" />
                  <span className="text-text" data-id="element-656" data-mp="37dg42vfiv8c6">{item}</span>
                </li>)}
            </ul>

            <p className="text-text font-medium text-navy" data-id="element-657" data-mp="2xq2o2ad0uer2">
              These are not random issues.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-658" data-mp="32q88p6kvj0zn">
              They are recurring signs that the journey, the handoffs, or the
              assumptions underneath them have not kept pace with growth.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What is usually happening underneath */}
      <section className="py-24 px-6 bg-paper" data-id="element-659" data-mp="3gj6apbrtnrc6">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-660" data-mp="3levc7knejib4">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-661" data-mp="3bnqy0n5jw1hc">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-662" data-mp="39id8199sezq">
              What is usually happening underneath
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-663" data-mp="1sqdzc11ej2tk">
            <p className="text-text" data-id="element-664" data-mp="1nsm4ay5gn0l5">
              Most of the time, the problem is not sitting neatly inside one
              department.
            </p>
            <p className="text-text" data-id="element-665" data-mp="22ggykiz49d0c">
              It sits in the seams — where the customer reality changed, but the
              business kept running old assumptions.
            </p>
            <p className="text-text" data-id="element-666" data-mp="19t3a799hhpru">
              Old qualification logic. Old handoffs. Old packaging. Old
              escalation paths. Old rules about who decides what, when, and with
              what information.
            </p>
            <p className="text-text" data-id="element-667" data-mp="1ernzwer40r21">
              So teams keep solving what they can see locally.
            </p>
            <p className="text-text" data-id="element-668" data-mp="2v5o07qtvaipt">
              Sales pushes for conversion. Delivery works around what should
              have been cleaner upstream. Customer success carries friction it
              did not create. Leadership keeps stepping in to interpret,
              resolve, and reconnect the dots.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-669" data-mp="14plnlqj3banq">
              The journey still moves.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-670" data-mp="18ouiwzts3gle">
              But not cleanly. And not with as much captured revenue as it
              should.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Revenue Journey Graphic */}
      <section className="py-16 px-6 bg-navy" data-id="element-671" data-mp="1nmv7f8sbuq5i">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-6xl mx-auto" data-id="element-672" data-mp="1nlk2v6r25edc">
          <p className="eyebrow text-white/40 mb-6 text-center" data-id="element-673" data-mp="21fumxas40kd2">
            The Revenue Journey
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/sfaH98wD5SnpeERBmaEXg9/Signal_Decay_(16_9).png" alt="Revenue journey signal decay — showing how value degrades across qualification, conversion, handoff, delivery, retention, and expansion" className="w-full h-auto" data-id="element-674" data-mp="26f7iql0yl1rs" />
        </motion.div>
      </section>

      {/* Six Taxes Intro + Accordion */}
      <section id="six-taxes" className="relative py-24 px-6 grain border-y border-seaglass/20 bg-[#8BA89F26] overflow-hidden" data-id="element-675" data-mp="22jd6k81e6y8u">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-676" data-mp="2zaf7yfyfe99z" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-677" data-mp="2uasty4arwp04">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-678" data-mp="3hzlru6ayh6xd">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-679" data-mp="3i16ktrhmght4">
              The Six Structural Taxes
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-680" data-mp="3d6it7jrbn7ab">
              Which pattern is creating the most drag right now?
            </h2>
            <p className="text-base text-navy font-medium mb-2" data-id="element-681" data-mp="1gbzc546d2bxu">
              The Six Structural Taxes are not stages of the journey.
            </p>
            <p className="text-base text-navy font-medium mb-2" data-id="element-682" data-mp="16jbi2h6rmm2t">
              They are the recurring ways growth starts breaking it once the
              business outgrows the old logic underneath it.
            </p>
            <p className="text-base text-text-muted mb-2" data-id="element-683" data-mp="xk6kbd312wrp">
              You may feel more than one. That is normal.
            </p>
            <p className="text-base text-text-muted mb-2" data-id="element-684" data-mp="io8uecjp6onx">
              The goal is not to label everything.
            </p>
            <p className="text-base text-text-muted" data-id="element-685" data-mp="2d0f4f80uuzen">
              The goal is to notice which pattern is loudest, where the dead
              ends are, and which problem is most worth fixing first.
            </p>
          </motion.div>

          <div className="space-y-0" data-id="element-686" data-mp="6cokbe8mn059">
            {taxes.map((tax, i) => <motion.div key={tax.anchor} id={tax.anchor} variants={fadeIn} className="border-b border-seaglass/20 last:border-0 scroll-mt-28" data-id="element-687" data-mp="1dldbo936jun" data-mpi={i}>
                <button onClick={() => setExpandedTax(expandedTax === i ? null : i)} className="w-full py-6 flex items-start justify-between text-left group" data-id="element-688" data-mp="b9ex9sp4xfmg">
                  <div className="flex-1 pr-6" data-id="element-689" data-mp="l0jdevqjr6iv">
                    <div className="flex items-start space-x-4 mb-2" data-id="element-690" data-mp="z13tvl8zoq97">
                      <span className="text-sm font-mono font-semibold text-seaglass uppercase tracking-wider" data-id="element-691" data-mp="yy7wby1203la">
                        {tax.num}
                      </span>
                      <h3 className="text-2xl font-serif font-semibold text-navy" data-id="element-692" data-mp="30s780ki38myi">
                        {tax.name}
                      </h3>
                    </div>

                    <p className="text-base text-text ml-10" data-id="element-693" data-mp="29ynbafqwmyv8">
                      {tax.definition}
                    </p>

                    {expandedTax !== i && <div className="ml-10 mt-3" data-id="element-694" data-mp="2jprq9fgkyjq2">
                        <p className="text-sm text-text-muted italic" data-id="element-695" data-mp="2jq9tiw1nty7h">
                          Feels like: {tax.feelsLike}
                        </p>
                      </div>}
                  </div>

                  <ChevronDown className={`w-5 h-5 text-seaglass flex-shrink-0 transition-transform duration-300 ${expandedTax === i ? 'rotate-180' : ''}`} data-id="element-696" data-mp="1r36yidw705lz" />
                </button>

                {expandedTax === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="pb-6 ml-10" data-id="element-697" data-mp="25zo8b0oqhc6f">
                    <div className="space-y-6" data-id="element-698" data-mp="r1a5zas931yh">
                      <p className="text-sm text-text-muted italic" data-id="element-699" data-mp="r0h7sy7cxkry">
                        Feels like: {tax.feelsLike}
                      </p>

                      <div data-id="element-700" data-mp="i0kf8lhuk1rm">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-701" data-mp="hz9b2kkhorzz">
                          Where it tends to show up in the revenue journey
                        </p>
                        <p className="text-base text-text" data-id="element-702" data-mp="82o3brh352sg">
                          {tax.whereShowsUp}
                        </p>
                      </div>

                      <div data-id="element-703" data-mp="27d601y9mziit">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-704" data-mp="22gfewwiz5wqj">
                          What it causes
                        </p>
                        <p className="text-base text-text" data-id="element-705" data-mp="2u9p7dj7dgex6">
                          {tax.whatItCauses}
                        </p>
                      </div>

                      <div data-id="element-706" data-mp="2kjxaujfppbq2">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-707" data-mp="2kj477rc1v2c5">
                          What leaders usually misread
                        </p>
                        <p className="text-base text-text" data-id="element-708" data-mp="2bka5xnnrxns9">
                          {tax.commonMisread}
                        </p>
                      </div>

                      <div data-id="element-709" data-mp="26mqu0uejtn3h">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-710" data-mp="21lb52r992wft">
                          What we would validate
                        </p>
                        <ul className="space-y-2" data-id="element-711" data-mp="1wob0kks1u1qt">
                          {tax.validate.map((item, k) => <li key={k} className="flex items-start space-x-2 text-base text-text" data-id="element-712" data-mp="3vxhrkm7lgznf" data-mpi={k}>
                              <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-713" data-mp="3bvlhuckca2q" />
                              <span data-id="element-714" data-mp="7bmgybd1bedl">{item}</span>
                            </li>)}
                        </ul>
                      </div>

                      <p className="text-sm text-text-muted" data-id="element-715" data-mp="h27dzud28aue">
                        If this feels like your loudest tax,{' '}
                        <Link to="/honest-conversation" className="text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-716" data-mp="3gkqjmoyhjz72">
                          book an Honest Conversation →
                        </Link>
                      </p>

                      <a href="#top" className="inline-flex items-center text-sm text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-717" data-mp="14tmls2nlvkd1">
                        Back to top →
                      </a>
                    </div>
                  </motion.div>}
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* What people usually do instead */}
      <section className="py-24 px-6 bg-paper" data-id="element-718" data-mp="p2vlx2jkgoqi">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-719" data-mp="p12azb1x7icp">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-720" data-mp="b3m2zv3z7giu">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-721" data-mp="b571fvtevjlz">
              What people usually do instead
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-722" data-mp="3eqcn7u25b4g0">
            <p className="text-text" data-id="element-723" data-mp="39skyf8wsryg1">
              Most companies do not rethink the journey when growth changes.
            </p>
            <p className="text-text" data-id="element-724" data-mp="1p98qya59zpeo">
              They keep running the old version harder.
            </p>
            <p className="text-text" data-id="element-725" data-mp="1u3v68oeha241">
              They push more demand into paths that no longer fit. They layer
              process on top of broken handoffs. They keep selling through
              outdated pricing or packaging. They miss expansion that the
              current model does not surface. They ask teams to work around
              friction the system should have removed.
            </p>
            <p className="text-text" data-id="element-726" data-mp="1z152amwdhc0u">That can hide the problem for a while.</p>
            <p className="text-text" data-id="element-727" data-mp="1z37pz0a1yt2e">But it does not remove the dead ends.</p>
            <p className="text-text" data-id="element-728" data-mp="2315hi9rapr8y">
              It just asks people and customers to carry them.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-729" data-mp="no2m3sy6qoh7">
              The better move is to find where the old logic no longer fits,
              where the dead ends are, and which lever creates the most revenue
              when pulled next.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-730" data-mp="y44u4fcf9rh7">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-731" data-mp="y2tsom6g8vt9">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-732" data-mp="1lu7k8pws0nd6">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-733" data-mp="1gtt8aws20t9v">The point</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-734" data-mp="1cukg3wcnx3gf">
              You do not fix everything at once.
            </h2>
            <p className="text-body text-text mb-3" data-id="element-735" data-mp="1enfhavgov3d">
              You identify which pattern is creating the most lost or missed
              revenue right now, find the constraint behind it, and redesign the
              right parts in the right order.
            </p>
            <p className="text-body text-text mb-3" data-id="element-736" data-mp="2h3u9zcei8wgz">
              That might mean a cleaner handoff. A tighter qualification rule. A
              pricing or packaging change. A better expansion path. A visibility
              fix that makes the real decision obvious. A dead end removed so
              customers can keep moving.
            </p>
            <p className="text-body text-text font-medium" data-id="element-737" data-mp="30r20g8kx9sbf">
              The point is not a bigger plan.
            </p>
            <p className="text-body text-text font-medium" data-id="element-738" data-mp="34u83768tt1d5">
              The point is finding the next domino.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="mt-8" data-id="element-739" data-mp="1af5kbrea8hj7">
            <Link to="/how-we-fix-it" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-740" data-mp="1agp1dk7riwm1">
              <span data-id="element-741" data-mp="2ipehtf38eh3x">See how we fix it</span>
              <ArrowRight className="w-4 h-4" data-id="element-742" data-mp="2dqbd5vr5ycwh" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* What to do next */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-743" data-mp="2rr46we9n53yj">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-744" data-mp="2mq81txdjzbrj">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-745" data-mp="2wonnhktt9stn">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-746" data-mp="3agkchg06nmlt">
              What to do next
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-747" data-mp="3ke8fet7y00f2">
            <p className="text-text" data-id="element-748" data-mp="3kerr3s701skn">
              You do not need a full-company reset to move forward.
            </p>
            <p className="text-text" data-id="element-749" data-mp="2vt49hl0q5z8">
              You need a clearer read on where the journey no longer fits, what
              is creating the drag, and which change would create the most
              leverage next.
            </p>

            <p className="text-text" data-id="element-750" data-mp="3p1zht3qpdbqu">In 45 minutes, we will:</p>

            <ul className="space-y-3 pl-1" data-id="element-751" data-mp="3u242l5yn8smv">
              {['pressure-test what you are seeing', 'clarify where the drag or missed opportunity may actually be coming from', 'decide whether this looks structural or not', 'make the next step clearer'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-752" data-mp="36e2vm1h45k9k" data-mpi={i}>
                  <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-753" data-mp="3batc2pwtyhvi" />
                  <span className="text-text" data-id="element-754" data-mp="2xc3fdbdxak7o">{item}</span>
                </li>)}
            </ul>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-755" data-mp="31dyh2gzrmzlz">
              <p className="eyebrow mb-2 text-seaglass" data-id="element-756" data-mp="31f9gy5q3bn7t">Possible outcomes</p>
              <ul className="space-y-2 text-sm text-text" data-id="element-757" data-mp="3hs67hlxus7cx">
                <li className="flex items-start space-x-2" data-id="element-758" data-mp="3ctl81oqfdrpj">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-759" data-mp="3cs097mx2vmlv" />
                  <span data-id="element-760" data-mp="37wup8tn8ki0f">You are fine. Keep pushing.</span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-761" data-mp="32v5d1aoa1yar">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-762" data-mp="2yulf44vvqqnk" />
                  <span data-id="element-763" data-mp="2u3mwuzrbziho">This is real, but it is not the right timing yet.</span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-764" data-mp="2p3qvl8a0q55t">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-765" data-mp="2p261fjrm8o8h" />
                  <span data-id="element-766" data-mp="2k70hgqhrxjn1">A Revenue Journey Diagnostic is warranted.</span>
                </li>
              </ul>
            </div>

            <p className="text-text font-medium" data-id="element-767" data-mp="2b4hem6dzn70s">
              If the issue is not structural, I will tell you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-768" data-mp="uns6rupdpn76">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-769" data-mp="upwkt5gzs2l6">
          <BlueprintGrid data-id="element-770" data-mp="zog5l2jun8by" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-771" data-mp="zlbr9cco6w7k">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-772" data-mp="bylvgsqwzxve">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-773" data-mp="gwdj3akj0xts">
              Spot the pattern.
            </span>{' '}
            <span className="text-white" data-id="element-774" data-mp="3kddno1f7yqcs">
              Then decide what is actually worth fixing.
            </span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-775" data-mp="3pboyofh936wl">
            You do not need a bigger plan first. You need a clearer read.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-776" data-mp="2z3cin64a369v">
            <span data-id="element-777" data-mp="2u36o9i6mj5o3">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-778" data-mp="2u1vm1mstu5z3" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-779" data-mp="2g9z060hr4sb5">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}